import { memo, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";

import { useFindingStore, Finding } from "stores/FindingStore";
import { useIgnoreStore } from "stores/IgnoreStore";

import { notifyError, notifySuccess } from "utility/notify";

import SelectedFindings from "./SelectedFindings";

// -----------------------------------------------------------------------------

const UnignoreDialog = memo(function UnignoreDialog({
  finding,
  onClose,
}: UnignoreDialogProps): JSX.Element {
  const [, ignoreActions] = useIgnoreStore();
  const [, findingActions] = useFindingStore();

  const [loading, setLoading] = useState(false);

  const onUnignore = () => {
    setLoading(true);
    ignoreActions.unignoreFinding({ uuid: finding.uuid, onSuccess, onError });
  };

  const onSuccess = () => {
    findingActions.resetDeeplinkFindings();
    notifySuccess("Unignored 1 finding");
    setLoading(false);
    onClose();
  };

  const onError = () => {
    notifyError("Something went wrong unignoring the finding");
    setLoading(false);
  };

  return (
    <Dialog open={true} onClose={onClose} fullWidth>
      <DialogTitle>Unignore Finding</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          You are choosing to unignore 1 selected finding:
        </Typography>
        <SelectedFindings findings={[finding]} />

        <Typography variant="body1">
          This will only impact this instance. Do you want to unignore?
        </Typography>
      </DialogContent>

      <DialogActions sx={{ pt: 0, pb: 3, px: 3 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          variant="contained"
          onClick={onUnignore}
        >
          Unignore
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
});

export default UnignoreDialog;

// -----------------------------------------------------------------------------

interface UnignoreDialogProps {
  finding: Finding;
  onClose: () => void;
}
